import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, createBlock as _createBlock, KeepAlive as _KeepAlive, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-ff2757a8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "dialog-header" }
const _hoisted_2 = { class: "header__left" }
const _hoisted_3 = {
  key: 1,
  class: "header-title"
}
const _hoisted_4 = {
  key: 0,
  class: "dialog-footer"
}


const __default__ = {
  name: 'appDialog'
}

import { ref, readonly, provide, markRaw, watch, reactive } from 'vue'
import type { Component } from 'vue'
import { NConfigProvider, NModal } from 'naive-ui'
import Icon from '@teams/common/components/icon'
import { providerKey } from './context'
import { useI18n } from 'vue-i18n'
import { dialogComponents } from '@/components/dialog'
import type { ShowOptions, DialogProvider, BackParams } from './types'
import { pick } from 'lodash-es'
import { isPromise } from '@/utils'

interface ComponentParams extends ShowOptions {
  component: Component
  componentName: string
}

type Dialog = {
  list: ComponentParams[]
  show(params: ShowOptions): Promise<void>
  closeCurrent(): void
  closeAll(): void
  back(params?: BackParams): Promise<void>
  showChange(show: boolean): void
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { t } = useI18n()

const DEFAULT_CONFIG = {
  showBackIcon: false, // 是否显示左上角返回icon
  showCloseIcon: true, // 是否显示右上角关闭icon
  footer: {
    okText: t('base.ok'),
    cancelText: t('base.cancel')
  },
  replace: false, // 是否替换当前显示的组件，否为push
  size: 'medium',
  maskClosable: false, // 点击遮罩是否关闭弹窗
  hideMask: false, // 是否隐藏遮罩
  componentProps: {},
  modalProps: {},
  componentEvents: {}
}

const refComponent = ref<{
  onOk?(): void | boolean | Promise<boolean>
  onClose?(): void
} | null>(null)

const visible = ref(false)
const keepAliveInclude = reactive<Set<string>>(new Set())
const componentMap = new Map()

const componentMounted = componentName => {
  // 将动态组件实例缓存起来（keepAlive在back后获取的组件实例是null）
  if (!componentMap.get(componentName) && refComponent.value) {
    componentMap.set(componentName, refComponent.value)
  }
}
// 从缓存列表删除数据
const removeComponentFromCache = (
  startIndex: number,
  endIndex?: number
): ComponentParams[] => {
  if (startIndex < 0) {
    startIndex = dialog.list.length + startIndex
  }
  if (endIndex === undefined) {
    endIndex = dialog.list.length
  }
  const result: ComponentParams[] = []
  dialog.list.slice(startIndex, endIndex).forEach(dialogItem => {
    result.push(dialogItem)
    keepAliveInclude.delete(dialogItem.componentName)
    componentMap.delete(dialogItem.componentName)
  })
  dialog.list = dialog.list.slice(0, startIndex)
  return result
}
const current = ref<ComponentParams>({} as ComponentParams)
const dialog: Dialog = {
  list: [],
  async show(params: ShowOptions) {
    const { name, component: paramsComponent } = params
    let definedComponent
    let component = paramsComponent
    if (!component && name) {
      definedComponent = dialogComponents[name]
      component = definedComponent?.body
    }
    if (!component) {
      console.error(`需要传入component 或在 dialog/index 注册组件并传入key`)
      return
    }
    const paramsKeys = [
      'title',
      'footer',
      'size',
      'replace',
      'showBackIcon',
      'showCloseIcon',
      'modalStyle',
      'modalProps',
      'componentProps',
      'componentEvents',
      'onClose',
      'maskClosable',
      'hideMask'
    ]
    const componentParams = pick(definedComponent, paramsKeys)
    const showParams = pick(params, paramsKeys)
    const config = Object.assign(
      {},
      DEFAULT_CONFIG,
      componentParams,
      showParams
    )
    // 获取异步组件里的组件
    // @ts-ignore
    if (component.__asyncLoader) {
      // @ts-ignore
      component = (await component.__asyncLoader()) as Component
    }
    // @ts-ignore
    const componentName = component.name || component.__name
    current.value = {
      componentName,
      title: config.title,
      showBackIcon: config.showBackIcon,
      showCloseIcon: config.showCloseIcon,
      footer: config.footer,
      size: config.size,
      component: markRaw(component),
      componentProps: config.componentProps,
      componentEvents: config.componentEvents,
      modalStyle: config.modalStyle,
      modalProps: config.modalProps,
      onClose: config.onClose,
      hideMask: config.hideMask,
      maskClosable: config.maskClosable
    }
    if (config.replace) {
      removeComponentFromCache(-1)
    }
    keepAliveInclude.add(componentName)
    dialog.list.push(current.value)
    visible.value = true
  },

  async back(params = {}) {
    const { componentName, component, componentProps } = params
    let _component = component
    let _componentName = componentName
    let targetIndex = dialog.list.length - 2
    // @ts-ignore
    if (component && component.__asyncLoader) {
      // @ts-ignore
      _component = (await component.__asyncLoader()) as Component
      // @ts-ignore
      _componentName = _component.name || _component.__name
    }
    if (_componentName) {
      targetIndex = dialog.list.findIndex(
        it => it.componentName === _componentName
      )
      if (!~targetIndex) {
        throw new Error(`弹窗【${_componentName}】不存在`)
      }
    }
    if (targetIndex < 0) {
      return dialog.closeAll()
    }
    current.value = dialog.list[targetIndex]
    current.value.componentProps = {
      ...current.value.componentProps,
      ...componentProps
    }
    // 将后面的从缓存列表去除
    removeComponentFromCache(targetIndex + 1)
  },
  // 关闭当前弹窗
  closeCurrent() {
    removeComponentFromCache(-1)
    const prev = dialog.list[dialog.list.length - 1]
    // 组件内的onClose
    if (refComponent.value && refComponent.value.onClose) {
      refComponent.value.onClose()
    }
    // show参数里的onClose
    if (current.value && current.value.onClose) {
      current.value?.onClose()
    }
    if (!prev) {
      visible.value = false
      return
    }
    componentMap.delete(current.value.componentName)
    current.value = prev
  },
  // 关闭所有弹窗
  closeAll() {
    dialog.list = []
    visible.value = false
  },
  showChange(v) {
    if (!v) {
      dialog.list = []
    }
  }
}
const footer = {
  ok() {
    // 执行组件内的onOk方法，返回false或promise返回faslse不关闭弹窗
    let autoClose = true
    if (refComponent.value && refComponent.value.onOk) {
      const result = refComponent.value.onOk()
      if (result === false) {
        autoClose = false
      } else if (isPromise(result)) {
        autoClose = false
        result.then(res => {
          res !== false && dialog.closeCurrent()
        })
      }
    }
    autoClose && dialog.closeCurrent()
  },
  cancel() {
    dialog.closeCurrent()
  }
}

watch(visible, isShow => {
  if (!isShow) {
    componentMap.clear()
    keepAliveInclude.clear()
  }
})

const provider: DialogProvider = {
  visible: readonly(visible),
  show: dialog.show,
  close: dialog.closeAll,
  back: dialog.back
}
provide(providerKey, provider)

return (_ctx: any,_cache: any) => {
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_space = _resolveComponent("n-space")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "default"),
    _createVNode(_unref(NConfigProvider), {
      namespace: `app-dialog-box ${
      current.value.componentName ? 'app-dialog-' + current.value.componentName : ''
    } ${current.value.hideMask ? 'app-dialog-mask--hide' : ''}`,
      abstract: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(NModal), _mergeProps({
          show: visible.value,
          "onUpdate:show": _cache[3] || (_cache[3] = ($event: any) => ((visible).value = $event)),
          "mask-closable": current.value.maskClosable
        }, current.value.modalProps, {
          "transform-origin": "center",
          preset: "dialog",
          class: [
        'app-dialog-container',
        `app-dialog-container--${current.value.size || 'auto'}`
      ],
          style: current.value.modalStyle,
          "onUpdate:show": dialog.showChange
        }), {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                (current.value.showBackIcon && dialog.list.length !== 1)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      class: "header__icon header__icon--back",
                      onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (dialog.back && dialog.back(...args)))
                    }, [
                      _createVNode(_unref(Icon), {
                        name: "dialogBack",
                        size: 16,
                        fill: "transparent"
                      })
                    ]))
                  : _createCommentVNode("", true),
                (current.value.title)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(current.value.title), 1))
                  : _createCommentVNode("", true)
              ]),
              (current.value.showCloseIcon)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: "header__icon header__icon--close",
                    onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (dialog.closeCurrent && dialog.closeCurrent(...args)))
                  }, [
                    _createVNode(_unref(Icon), {
                      name: "close",
                      size: 16
                    })
                  ]))
                : _createCommentVNode("", true)
            ], 512), [
              [_vShow, current.value.title || current.value.showCloseIcon]
            ]),
            (_openBlock(), _createBlock(_KeepAlive, {
              include: Array.from(keepAliveInclude)
            }, [
              (_openBlock(), _createBlock(_resolveDynamicComponent(current.value.component), _mergeProps({
                key: current.value.componentName
              }, current.value.componentProps, _toHandlers(current.value.componentEvents), {
                ref_key: "refComponent",
                ref: refComponent,
                onClose: dialog.closeCurrent,
                onCloseAll: dialog.closeAll,
                onBack: dialog.back,
                onVnodeMounted: _cache[2] || (_cache[2] = ($event: any) => (componentMounted(current.value.componentName)))
              }), null, 16, ["onClose", "onCloseAll", "onBack"]))
            ], 1032, ["include"])),
            (current.value.footer)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_component_n_space, { size: 16 }, {
                    default: _withCtx(() => [
                      (current.value.footer.okText)
                        ? (_openBlock(), _createBlock(_component_n_button, {
                            key: 0,
                            class: "footer-btn",
                            round: "",
                            type: "primary",
                            onClick: footer.ok
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(current.value.footer.okText), 1)
                            ]),
                            _: 1
                          }, 8, ["onClick"]))
                        : _createCommentVNode("", true),
                      (current.value.footer.cancelText)
                        ? (_openBlock(), _createBlock(_component_n_button, {
                            key: 1,
                            class: "footer-btn",
                            round: "",
                            onClick: footer.cancel
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(current.value.footer.cancelText), 1)
                            ]),
                            _: 1
                          }, 8, ["onClick"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 16, ["show", "mask-closable", "class", "style", "onUpdate:show"])
      ]),
      _: 1
    }, 8, ["namespace"])
  ], 64))
}
}

})